import { Box, Typography, Grid, Paper } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PeopleIcon from '@mui/icons-material/People';
import MessageIcon from '@mui/icons-material/Message';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { SectionCard } from '../../components/common';

export const AdminDashboardPage = () => {
  // Mock metrics
  const metrics = {
    totalUsers: 1247,
    activePatients: 892,
    nutritionists: 34,
    messagesSent: 3456,
    avgResponseTime: '4.2 hours',
    aiApprovalRate: 87,
    patientEngagement: 76,
    weekOverWeekGrowth: 12
  };

  const MetricCard = ({ title, value, subtitle, icon, color }: {
    title: string;
    value: string | number;
    subtitle: string;
    icon: React.ReactNode;
    color: string;
  }) => (
    <Paper
      elevation={2}
      sx={{
        p: 3,
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }}
    >
      <Box
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: `${color}.light`,
          color: `${color}.main`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="h4" fontWeight={700}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      </Box>
    </Paper>
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        System Dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Organization-wide analytics and key performance indicators
      </Typography>

      <Grid container spacing={3}>
        {/* Key Metrics */}
        <Grid item xs={12} md={6} lg={3}>
          <MetricCard
            title="Total Users"
            value={metrics.totalUsers.toLocaleString()}
            subtitle={`+${metrics.weekOverWeekGrowth}% this week`}
            icon={<PeopleIcon sx={{ fontSize: 32 }} />}
            color="primary"
          />
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <MetricCard
            title="Active Patients"
            value={metrics.activePatients.toLocaleString()}
            subtitle="Currently enrolled"
            icon={<TrendingUpIcon sx={{ fontSize: 32 }} />}
            color="success"
          />
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <MetricCard
            title="Messages Sent"
            value={metrics.messagesSent.toLocaleString()}
            subtitle="This month"
            icon={<MessageIcon sx={{ fontSize: 32 }} />}
            color="info"
          />
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <MetricCard
            title="AI Approval Rate"
            value={`${metrics.aiApprovalRate}%`}
            subtitle="Human review success"
            icon={<CheckCircleIcon sx={{ fontSize: 32 }} />}
            color="warning"
          />
        </Grid>

        {/* Performance Metrics */}
        <Grid item xs={12} md={6}>
          <SectionCard title="System Performance">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Average Response Time
                </Typography>
                <Typography variant="h5" color="primary.main">
                  {metrics.avgResponseTime}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Patient Engagement Rate
                </Typography>
                <Typography variant="h5" color="success.main">
                  {metrics.patientEngagement}%
                </Typography>
              </Box>
            </Box>
          </SectionCard>
        </Grid>

        {/* Staff Overview */}
        <Grid item xs={12} md={6}>
          <SectionCard title="Healthcare Staff">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Registered Nutritionists</Typography>
                <Typography variant="h6" color="primary.main">
                  {metrics.nutritionists}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Avg. Patients per Nutritionist</Typography>
                <Typography variant="h6">
                  {Math.round(metrics.activePatients / metrics.nutritionists)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Messages per Patient</Typography>
                <Typography variant="h6">
                  {(metrics.messagesSent / metrics.activePatients).toFixed(1)}
                </Typography>
              </Box>
            </Box>
          </SectionCard>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12}>
          <SectionCard title="System Activity" subtitle="Last 30 days">
            <Typography variant="body2" color="text.secondary">
              Detailed analytics charts coming soon...
            </Typography>
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
};
